import { Injectable } from '@angular/core';
import { User } from '@csx/security';

import { ChangeRequestType } from '../models/change-request-type.enum';
import { ChangeRequest } from '../models/changerequest';
import { HttpMethod } from '../models/http';
import { Status } from '../models/status.enum';
import { ATMEventData, EventBusService, EventType } from '../services/eventbus.service';
import { DataShareService } from './data-share.service';
import { HttpService } from './http.service';
import { LocalStorageService } from './localstorage.service';
import { EmployeeInCharge } from '../models/employeeInCharge';
import { ProxyUser } from '../models/proxyuser';
import { TrackAuthority } from '../models/trackauthority';
import { EICSTatus } from '../models/eic-status.enum';
import { SharingChangeModel } from '../models/sharing-change-model';

@Injectable()
export class ChangeRequestService {
  constructor(
    private httpService: HttpService,
    private eventBus: EventBusService,
    private dataShareService: DataShareService,
    private localStorageService: LocalStorageService,
    private user: User,
    private proxy: ProxyUser
  ) {
    this.localStorageService.storeUserDetail(this.user);
  }

  postChangeRequest(
    changeRequest: ChangeRequest,
    trackAuthority: TrackAuthority,
    secondaryName?
  ): Promise<void> {
    let headers;
    if (this.proxy.racf !== undefined) {
      headers = { 'proxiedRacf': this.proxy.racf }
    }
    let request = this.httpService.request({
      method: HttpMethod.Post,
      headers: headers,
      url: 'v2/track-authorities/' + trackAuthority.id + '/change-requests',
      isUserAction: true,
      successMessageOverride: n => {
        if (trackAuthority.isAuthorityShared || changeRequest.type === Status.TYPE_SHARE.toString()) {
          let updatedChangeRequest: ChangeRequest = {
            trackAuthorityId: trackAuthority.id,
            authorityChangeRequestId: Math. floor(1000 + Math. random() * 9000),
            changeUserAcceptedMessages: [],
            type: changeRequest.type
          };
          if (changeRequest.type === ChangeRequestType.REPORTBY || changeRequest.type === ChangeRequestType.MODIFY) {
            updatedChangeRequest.authorityChangeRequestStatus = Status.CHANGE_PROPOSED.toString();
            trackAuthority.authorityChangeRequests.push(updatedChangeRequest);
            trackAuthority.employeeInCharges.forEach(item => {
              if (item.status === EICSTatus.ACTIVE && !item.primary) {
                item.lastChangeRequestStatus= EICSTatus.PENDING;
              }
            });
            this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(trackAuthority, () => { }));
            return `Authority #${trackAuthority.ec1Number}: Proposed Modification shared with other users`;
          } else if (changeRequest.type === Status.TYPE_SHARE.toString()) {
            return `Authority #${trackAuthority.ec1Number}: Sharing request initiated`;
          } else if (changeRequest.type === Status.TYPE_UNSHARE.toString() && secondaryName) {
            updatedChangeRequest.authorityChangeRequestStatus = Status.UNSHARING_INITIATED.toString();
            updatedChangeRequest.sharingChangeModel = {
              secondaryEICRacf: 'Y2384'
            };
            trackAuthority.authorityChangeRequests.push(updatedChangeRequest);
            trackAuthority.employeeInCharges.forEach(item => {
              if (item.status === EICSTatus.ACTIVE && item.eicUserId === secondaryName.split(' - ')[0]) {
                item.status= EICSTatus.PENDING;
              }
            });
            this.eventBus.fireEvent(EventType.AUTHORITY_UPDATED_EVENT, new ATMEventData(trackAuthority, () => { }));
            return `Authority #${trackAuthority.ec1Number}: Unsharing initiated with ${secondaryName}`;
          }
          return null;
        } else {
          let successMessage;
          if (changeRequest.type === 'REPORTBY') {
            successMessage = 'Report By request for Track Authority ' + trackAuthority.ec1Number + ' created successfully';
          } else if (changeRequest.type === 'MODIFY') {
            successMessage = 'Modify request for Track Authority ' + trackAuthority.ec1Number + ' created successfully';
          }
          return successMessage;
        }
      },
      errorMessageOverride: n => {
        if (changeRequest.type === Status.TYPE_SHARE.toString() || changeRequest.type === Status.TYPE_UNSHARE.toString()) {
          if (n && n.errorMessage) {
            return n.errorMessage;
          }
          return null;
        } else {
          let errorMessage = 'Change Request for Track Authority ' + trackAuthority.ec1Number + ' failed';

          if (n.responseObject !== undefined && n.responseObject.length !== undefined) {
            errorMessage += ': ';
            for (let i = 0; i < n.responseObject.length; i++) {
              let error = n.responseObject[i];
              if (error.field !== undefined && error.message !== undefined) {
                if (errorMessage.length > 0) {
                  errorMessage += '\n';
                }
                errorMessage += 'Field "' + error.field + '": ' + error.message;
              }
            }
          }
          return errorMessage.length === 0 ? n.errorMessage : errorMessage;
        }
      },
      body: changeRequest
    });

    return request.toPromise().then(
      response => {
        this.eventBus.fireEvent(EventType.RESET_POLL_EVENT, new ATMEventData(null, () => ''));
        return response.responseObject;
      },
      error => {}
    );
  }
}
